import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoPayCard } from '@/components/payments/AutoPayCard';
import { AddPaymentAccountModal } from '@/components/payments/accounts/AddPaymentAccountModal';
import { DefaultPaymentMethodDisplay } from '@/components/payments/DefaultPaymentMethodDisplay';
import { MembershipBenefits } from '@/components/payments/MembershipBenefits';
import { OneTimePaymentModal } from '@/components/payments/oneTimePayment/OneTimePaymentModal';
import { PaymentSummaryCard } from '@/components/payments/paymentSummary/PaymentSummaryCard';
import { RecentTransactionsSection } from '@/components/payments/RecentTransactionsSection';
import { UpcomingPaymentsSection } from '@/components/payments/UpcomingPaymentsSection';

interface PaymentCenterPageProps {
  /** Deep-link (`?turnOnAutopay=true`): opens AutoPay enrollment as soon as the card mounts. */
  autoOpenEnrollment?: boolean;
  /** Fired when enrollment is dismissed, so the page can drop the deep-link param. */
  onEnrollmentClose?: () => void;
  /** Deep-link (`?makePayment=true`): opens the one-time payment modal on arrival. */
  autoOpenPayment?: boolean;
  /** Fired when the payment modal is dismissed, so the page can drop the deep-link param. */
  onPaymentClose?: () => void;
  /** Sends the member to the full Past Transactions tab from the Recent Transactions widget. */
  onViewAllTransactions: () => void;
  /** Sends the member to the Payment Accounts tab from the default-method strip. */
  onManagePaymentAccounts: () => void;
}

export function PaymentCenterPage({
  autoOpenEnrollment,
  onEnrollmentClose,
  autoOpenPayment,
  onPaymentClose,
  onViewAllTransactions,
  onManagePaymentAccounts,
}: PaymentCenterPageProps) {
  const { t } = useTranslation('members');

  // The page owns the payment modal rather than the Upcoming Payments section, so the default-method
  // strip (and the balance summary card, once it exists) triggers the same instance.
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);

  /**
   * Bumped whenever a *new* payment flow starts, and used as the modal's `key` so it remounts with
   * fresh state. Returning from the add-account detour deliberately leaves it alone, which keeps the
   * member's place: the interstitial stays dismissed and anything they typed survives.
   */
  const [paymentSession, setPaymentSession] = useState(0);

  const startPayment = useCallback(() => {
    setPaymentSession((session) => session + 1);
    setPaymentOpen(true);
  }, []);

  // Keyed on the flag, not on mount, so clearing the URL param cannot reopen the modal.
  useEffect(() => {
    if (autoOpenPayment) startPayment();
  }, [autoOpenPayment, startPayment]);

  const handlePaymentOpenChange = useCallback(
    (open: boolean) => {
      setPaymentOpen(open);
      if (!open) onPaymentClose?.();
    },
    [onPaymentClose],
  );

  /**
   * Returns from the add-account detour, on both success and Cancel. Reopens without bumping the
   * session, so the modal is not remounted and the member picks up where they left off. A new
   * account is saved as the member's default, so the picker preselects it once
   * `mySavedPaymentMethods` refetches.
   */
  const returnToPayment = useCallback(() => {
    setAddAccountOpen(false);
    setPaymentOpen(true);
  }, []);

  /**
   * Dismissing the add-account screen with X, Escape or the overlay ends the whole payment flow, so
   * the deep-link param goes with it. Cancel is routed to `returnToPayment` instead, which keeps the
   * param because the flow is still running.
   */
  const handleAddAccountOpenChange = useCallback(
    (open: boolean) => {
      setAddAccountOpen(open);
      if (!open) onPaymentClose?.();
    },
    [onPaymentClose],
  );

  return (
    <section>
      <h2 className="text-h2 font-semibold text-foreground">{t('payments.pages.paymentCenter.sectionTitle')}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('payments.pages.paymentCenter.sectionDescription')}</p>
      {/* @container-driven, not viewport-driven, so the split tracks this section's own width (e.g.
          a narrower embedded context) rather than the screen. Matches the design's actual
          proportions — PaymentSummaryCard at 2/3, AutoPayCard at 1/3 — not an equal-thirds grid.
          The @container declaration has to live on its own wrapper, separate from the grid that
          uses @3xl:/@5xl: variants: a container query can't resolve against the element it's
          declared on, only against an ancestor — so the grid needs a dedicated parent container. */}
      <div className="@container mt-6 min-w-0">
        <div className="grid grid-cols-4 gap-4 @3xl:grid-cols-8 @3xl:gap-6 @5xl:grid-cols-12 @5xl:items-stretch">
          <PaymentSummaryCard className="col-span-4 @3xl:col-span-8 @5xl:col-span-8" />
          <AutoPayCard
            className="col-span-4 @3xl:col-span-8 @5xl:col-span-4"
            autoOpenEnrollment={autoOpenEnrollment}
            onEnrollmentClose={onEnrollmentClose}
          />
        </div>
      </div>
      <DefaultPaymentMethodDisplay
        className="mt-6"
        onMakePayment={startPayment}
        onManageMethods={onManagePaymentAccounts}
      />
      <UpcomingPaymentsSection onMakePayment={startPayment} />
      <RecentTransactionsSection onViewAll={onViewAllTransactions} />
      <MembershipBenefits />

      <OneTimePaymentModal
        key={paymentSession}
        open={paymentOpen}
        onOpenChange={handlePaymentOpenChange}
        onAddAccount={() => {
          setPaymentOpen(false);
          setAddAccountOpen(true);
        }}
      />
      <AddPaymentAccountModal
        open={addAccountOpen}
        onOpenChange={handleAddAccountOpenChange}
        onSuccess={returnToPayment}
        onCancel={returnToPayment}
      />
    </section>
  );
}
